import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static get targets() {
    return [
      'predictiveResults',
      'targetInput',
      'destinationUid',
      'lat',
      'lng',
      'results',
      'noResults',
      'form',
      'submitButton',
      'enabledIcon',
      'disabledIcon',
    ];
  }

  static values = {
    getUrl: String,
    defaultValue: String,
    placeholder: String,
  }

  connect() {
    this.targetInputTarget.setAttribute('hx-get', this.getUrlValue);
    this.targetInputTarget.value = this.defaultValueValue || '';
    this.targetInputTarget.setAttribute('placeholder', this.placeholderValue || '');

    this.hideNoResultsTimeout = null;
    this.activeIndex = -1;
    this.initializeEventListeners();
    this.setupCombobox();
    this.validSelection = true;
    // Value of the last picked suggestion. A debounced keyup request from earlier
    // typing can land just after a selection and re-open the dropdown; we ignore
    // any response whose input value still matches this (WCAG 4.1.2 — no spurious
    // aria-expanded="true" after selecting).
    this.lastSelectedValue = null;

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside);
  }

  /**
   * Wire the text input + results dropdown as an ARIA combobox (WAI-ARIA 1.2
   * autocomplete/list pattern). Done in JS so it covers both the desktop and
   * mobile instances and keeps option ids unique per instance (the shared
   * results partial can't know which listbox it lands in).
   */
  setupCombobox() {
    const input = this.targetInputTarget;
    const listbox = this.hasPredictiveResultsTarget ? this.predictiveResultsTarget : null;

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-haspopup', 'listbox');
    if (!input.hasAttribute('aria-label') && !input.getAttribute('aria-labelledby')) {
      input.setAttribute('aria-label', 'Search destinations or properties');
    }

    if (listbox) {
      input.setAttribute('aria-controls', listbox.id);
      listbox.setAttribute('role', 'listbox');
      listbox.setAttribute('aria-label', 'Search suggestions');
    }

    // A polite live region announces how many suggestions arrived, since the
    // dropdown appears/updates without moving focus (WCAG 4.1.3).
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('role', 'status');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.className = 'sr-only';
    this.element.appendChild(this.liveRegion);

    input.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleSubmit(event) {
    // Allow submission if user has a valid selection OR typed >= 3 chars
    // (server-side geocode fallback handles unselected text queries)
    if (!this.validSelection) {
      const inputValue = (this.targetInputTarget.value || '').trim();
      if (inputValue.length < 3) {
        event.preventDefault();
        return false;
      }
    }
    return true;
  }

  initializeEventListeners() {
    this.element.addEventListener('htmx:afterOnLoad', this.handleAfterOnLoad.bind(this));
    this.element.addEventListener('htmx:afterSwap', this.handleAfterSwap.bind(this));
    this.formTarget.addEventListener('submit', this.handleSubmit.bind(this));
  }

  handleAfterOnLoad(event) {
    /* Handle the afterOnLoad event from htmx */

    const targetId = event.detail.target.id;
    // Support both desktop and mobile predictive results
    if ((targetId !== 'predictive-results' && targetId !== 'mobile-predictive-results') || !this.predictiveResultsTarget) return;

    // Stale response arriving after a selection — keep the dropdown closed.
    if (this.isStaleAfterSelection()) {
      this.predictiveResultsTarget.classList.add('hidden');
      this.setExpanded(false);
      return;
    }

    if (this.targetInputTarget.value === '') {
      this.predictiveResultsTarget.classList.add('hidden');
      this.setExpanded(false);
      this.validSelection = true;
      // Make button valid when input is empty - with null checks
      if (this.hasDisabledIconTarget) {
        this.disabledIconTarget.classList.add('hidden');
      }
      if (this.hasEnabledIconTarget) {
        this.enabledIconTarget.classList.remove('hidden');
      }
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.classList.remove('bg-[#808080]');
        this.submitButtonTarget.classList.add('bg-[#0FEED7]');
      }
    } else {
      this.toggleResultsVisibility();
    }
  }

  toggleResultsVisibility() {
    /* Show the predictive results dropdown — both results and no-results states stay visible
       until the user selects a result, clears the input, or clicks outside. */
    clearTimeout(this.hideNoResultsTimeout);
    this.predictiveResultsTarget.classList.remove('hidden');
    this.setExpanded(true);
  }

  handleAfterSwap(event) {
    /* Handle the afterSwap event from htmx */
    const targetId = event.detail.target.id;
    if (targetId === 'predictive-results' || targetId === 'mobile-predictive-results') {
      // Stale response after a selection: don't re-decorate/re-open or clobber the
      // valid-selection state.
      if (this.isStaleAfterSelection()) return;
      this.addClickListenersToResults();
      this.decorateOptions();
      this.announceResults();
      this.validSelection = false;
      // Clear destination UUID and lat/lng when user types new text
      if (this.hasDestinationUidTarget) {
        this.destinationUidTarget.value = '';
      }
      if (this.hasLatTarget) {
        this.latTarget.value = '';
      }
      if (this.hasLngTarget) {
        this.lngTarget.value = '';
      }
      // Make button invalid - with null checks
      if (this.hasEnabledIconTarget) {
        this.enabledIconTarget.classList.add('hidden');
      }
      if (this.hasDisabledIconTarget) {
        this.disabledIconTarget.classList.remove('hidden');
      }
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.classList.remove('bg-[#0FEED7]');
        this.submitButtonTarget.classList.add('bg-[#808080]');
      }
    }
  }

  /**
   * Turn the freshly-swapped result anchors into listbox options with unique,
   * per-instance ids, and reset the keyboard-active option.
   */
  decorateOptions() {
    const listboxId = this.hasPredictiveResultsTarget ? this.predictiveResultsTarget.id : 'predictive';
    this.resultsTargets.forEach((item, index) => {
      item.setAttribute('role', 'option');
      item.setAttribute('id', `${listboxId}-option-${index}`);
      item.setAttribute('tabindex', '-1');
      item.setAttribute('aria-selected', 'false');
    });
    this.clearActive();
  }

  announceResults() {
    if (!this.liveRegion) return;
    const count = this.resultsTargets.length;
    if (this.targetInputTarget.value.trim() === '') {
      this.liveRegion.textContent = '';
    } else if (count > 0) {
      this.liveRegion.textContent = `${count} suggestion${count === 1 ? '' : 's'} available.`;
    } else {
      this.liveRegion.textContent = 'No results found. Try a different search term.';
    }
  }

  setExpanded(expanded) {
    this.targetInputTarget.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (!expanded) this.clearActive();
  }

  // True when an HTMX response is stale: the user has already picked a suggestion
  // and hasn't changed the input since, so this response is from an earlier
  // (debounced) keystroke and must not re-open the dropdown.
  isStaleAfterSelection() {
    return this.lastSelectedValue !== null
      && this.targetInputTarget.value === this.lastSelectedValue;
  }

  /* --- Keyboard navigation (arrow keys move the active option; Enter selects;
     Escape closes) using aria-activedescendant so focus stays in the input. --- */
  handleKeydown(event) {
    const options = this.resultsTargets;
    const isOpen = this.hasPredictiveResultsTarget
      && !this.predictiveResultsTarget.classList.contains('hidden');

    switch (event.key) {
      case 'ArrowDown':
        if (!isOpen || options.length === 0) return;
        event.preventDefault();
        this.setActive(this.activeIndex < options.length - 1 ? this.activeIndex + 1 : 0);
        break;
      case 'ArrowUp':
        if (!isOpen || options.length === 0) return;
        event.preventDefault();
        this.setActive(this.activeIndex > 0 ? this.activeIndex - 1 : options.length - 1);
        break;
      case 'Enter':
        // Only intercept Enter when an option is highlighted; otherwise let the
        // form submit (the server geocodes free-text queries).
        if (isOpen && this.activeIndex >= 0 && options[this.activeIndex]) {
          event.preventDefault();
          options[this.activeIndex].click();
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.predictiveResultsTarget.classList.add('hidden');
          this.setExpanded(false);
        }
        break;
      default:
        break;
    }
  }

  setActive(index) {
    const options = this.resultsTargets;
    options.forEach((opt, i) => {
      const active = i === index;
      opt.setAttribute('aria-selected', active ? 'true' : 'false');
      opt.classList.toggle('bg-[#F5F3F1]', active);
    });
    this.activeIndex = index;
    const activeOpt = options[index];
    if (activeOpt) {
      this.targetInputTarget.setAttribute('aria-activedescendant', activeOpt.id);
      activeOpt.scrollIntoView({ block: 'nearest' });
    }
  }

  clearActive() {
    this.activeIndex = -1;
    this.targetInputTarget.removeAttribute('aria-activedescendant');
    this.resultsTargets.forEach((opt) => {
      opt.setAttribute('aria-selected', 'false');
      opt.classList.remove('bg-[#F5F3F1]');
    });
  }

  handleClickOutside(event) {
    /* Close dropdown when clicking outside the search bar */
    if (!this.hasPredictiveResultsTarget) return;
    if (this.predictiveResultsTarget.classList.contains('hidden')) return;

    const clickedInsideResults = this.predictiveResultsTarget.contains(event.target);
    const clickedInsideInput = this.targetInputTarget.contains(event.target);

    if (!clickedInsideResults && !clickedInsideInput) {
      this.predictiveResultsTarget.classList.add('hidden');
      this.setExpanded(false);
    }
  }

  addClickListenersToResults() {
    /* Add click event listeners to each result item */
    this.resultsTargets.forEach((item) => {
      item.addEventListener('click', (event) => {
        const resultType = item.getAttribute('data-type');
        const href = item.getAttribute('href');

        // For properties with href, let the browser navigate naturally
        if (resultType === 'property' && href) {
          // Don't prevent default - allow navigation
          return;
        }

        // For destinations and geocoded results, set the input value and mark as valid
        event.preventDefault();
        this.targetInputTarget.value = item.getAttribute('data-value');
        this.lastSelectedValue = this.targetInputTarget.value;

        if (resultType === 'geocoded') {
          // Geocoded result: set lat/lng, clear destination UUID
          if (this.hasLatTarget) {
            this.latTarget.value = item.getAttribute('data-lat') || '';
          }
          if (this.hasLngTarget) {
            this.lngTarget.value = item.getAttribute('data-lng') || '';
          }
          if (this.hasDestinationUidTarget) {
            this.destinationUidTarget.value = '';
          }
        } else {
          // Destination result: set UUID, clear lat/lng
          if (this.hasDestinationUidTarget) {
            this.destinationUidTarget.value = item.getAttribute('data-uuid') || '';
          }
          if (this.hasLatTarget) {
            this.latTarget.value = '';
          }
          if (this.hasLngTarget) {
            this.lngTarget.value = '';
          }
        }
        this.validSelection = true;

        // Make button valid - with null checks
        if (this.hasDisabledIconTarget) {
          this.disabledIconTarget.classList.add('hidden');
        }
        if (this.hasEnabledIconTarget) {
          this.enabledIconTarget.classList.remove('hidden');
        }
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.classList.remove('bg-[#808080]');
          this.submitButtonTarget.classList.add('bg-[#0FEED7]');
        }
        this.predictiveResultsTarget.classList.add('hidden');
        this.setExpanded(false);
      });
    });
  }
}
